class CollectionProductSearch {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.productsPerPage = Number(root.dataset.productsPerPage) || 16;
    this.requestId = 0;
    this.isApplyingResults = false;

    this.onSubmit = this.onSubmit.bind(this);
    this.onSearchInput = this.onSearchInput.bind(this);
    this.onNativeSearch = this.onNativeSearch.bind(this);
    this.onGridUpdated = this.onGridUpdated.bind(this);
    this.onPaginationClick = this.onPaginationClick.bind(this);

    document.addEventListener('submit', this.onSubmit);
    this.root.addEventListener('input', this.onSearchInput, true);
    this.root.addEventListener('search', this.onNativeSearch, true);
    document.addEventListener('collection:grid-updated', this.onGridUpdated);
    document.addEventListener('click', this.onPaginationClick);

    this.syncInputs();
    this.applySearchFromUrl();
  }

  onSearchInput(event) {
    if (!event.target.matches('[data-collection-search-input]')) return;

    // The desktop search field sits beside Dawn's sort form. Prevent Dawn's
    // debounced facet input handler from treating search typing as a sort/filter update.
    event.stopPropagation();
  }

  onNativeSearch(event) {
    if (!event.target.matches('[data-collection-search-input]')) return;
    if (event.target.value.trim()) return;

    const params = new URLSearchParams(window.location.search);
    params.delete('collection_search');
    params.delete('search_page');
    params.delete('page');

    history.pushState({ searchParams: params.toString() }, '', this.buildBrowserUrl(params));
    this.syncInputs();
    this.restoreCollection();
  }

  get searchTerm() {
    return new URLSearchParams(window.location.search).get('collection_search')?.trim() || '';
  }

  get clientPage() {
    return Math.max(1, Number(new URLSearchParams(window.location.search).get('search_page')) || 1);
  }

  syncInputs() {
    document.querySelectorAll('[data-collection-search-input]').forEach((input) => {
      input.value = this.searchTerm;
    });
  }

  onSubmit(event) {
    const form = event.target.closest('[data-collection-search-form]');
    if (!form) return;

    event.preventDefault();

    const input = document.querySelector(`[data-collection-search-input][form="${form.id}"]`)
      || form.querySelector('[data-collection-search-input]');
    const params = new URLSearchParams(window.location.search);
    const term = input?.value.trim() || '';

    params.delete('page');
    params.delete('search_page');

    if (term) {
      params.set('collection_search', term);
    } else {
      params.delete('collection_search');
    }

    history.pushState({ searchParams: params.toString() }, '', this.buildBrowserUrl(params));
    this.syncInputs();

    if (term) {
      this.search(term, 1);
    } else {
      this.restoreCollection();
    }
  }

  onGridUpdated() {
    if (this.isApplyingResults) return;
    this.syncInputs();
    if (!this.searchTerm) return;
    this.search(this.searchTerm, this.clientPage);
  }

  onPaginationClick(event) {
    const button = event.target.closest('[data-collection-search-load-more]');
    if (!button) return;

    event.preventDefault();
    const page = Number(button.dataset.collectionSearchPage);
    const params = new URLSearchParams(window.location.search);
    params.set('search_page', String(page));
    history.pushState({ searchParams: params.toString() }, '', this.buildBrowserUrl(params));
    this.renderMatches(this.matches || [], page);
  }

  applySearchFromUrl() {
    if (this.searchTerm) {
      this.search(this.searchTerm, this.clientPage);
    }
  }

  async search(term, page) {
    const requestId = ++this.requestId;
    this.setLoading(true);

    try {
      const firstDocument = await this.fetchPage(1);
      const pageCount = this.getPageCount(firstDocument);
      const documents = [firstDocument];

      if (pageCount > 1) {
        const remainingPages = Array.from({ length: pageCount - 1 }, (_, index) => this.fetchPage(index + 2));
        documents.push(...await Promise.all(remainingPages));
      }

      if (requestId !== this.requestId) return;

      const normalizedTerms = term
        .toLocaleLowerCase()
        .split(/\s+/)
        .filter(Boolean);
      this.matches = documents.flatMap((document) => {
        return Array.from(document.querySelectorAll('#product-grid > .grid__item'));
      }).filter((item) => {
        const searchableContent = [
          item.dataset.productSearchTitle,
          item.dataset.productSearchTags,
          item.dataset.productSearchSkus,
        ]
          .filter(Boolean)
          .join(' ')
          .toLocaleLowerCase();

        return normalizedTerms.every((searchTerm) => searchableContent.includes(searchTerm));
      });

      this.renderMatches(this.matches, page);
    } catch (error) {
      console.error('Collection search failed:', error);
    } finally {
      if (requestId === this.requestId) this.setLoading(false);
    }
  }

  async fetchPage(page) {
    const params = new URLSearchParams(window.location.search);
    params.delete('search_page');
    params.set('page', String(page));
    params.set('section_id', this.sectionId);

    const response = await fetch(`${window.location.pathname}?${params.toString()}`);
    if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  getPageCount(document) {
    return Math.max(1, Number(document.querySelector('[data-collection-page-count]')?.dataset.collectionPageCount) || 1);
  }

  renderMatches(matches, requestedPage) {
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const pageCount = Math.max(1, Math.ceil(matches.length / this.productsPerPage));
    const currentPage = Math.min(Math.max(1, requestedPage), pageCount);
    const visibleMatches = matches.slice(0, currentPage * this.productsPerPage);

    this.isApplyingResults = true;
    grid.innerHTML = '';

    if (visibleMatches.length) {
      visibleMatches.forEach((item) => grid.append(item.cloneNode(true)));
    } else {
      grid.innerHTML = `
        <li class="collection-search__empty grid__item">
          <p>No products found for “${this.escapeHtml(this.searchTerm)}”.</p>
        </li>
      `;
    }

    this.renderCount(matches.length);
    this.renderShownCount(visibleMatches.length, matches.length);
    this.renderPagination(grid, currentPage, pageCount);
    this.isApplyingResults = false;
  }

  renderCount(count) {
    const text = `All Products (${count})`;
    const desktopCount = document.getElementById('ProductCountDesktop');
    const mobileCount = document.getElementById('ProductCount');

    if (desktopCount) desktopCount.textContent = text;
    if (mobileCount) mobileCount.textContent = text;
  }

  renderShownCount(shown, total) {
    const indicator = document.querySelector('[data-collection-shown-count]');
    if (!indicator) return;

    const shownValue = indicator.querySelector('[data-shown-products]');
    const totalValue = indicator.querySelector('[data-total-products]');
    if (shownValue) shownValue.textContent = String(shown);
    if (totalValue) totalValue.textContent = String(total);
    indicator.dataset.totalProducts = String(total);
  }

  renderPagination(grid, currentPage, pageCount) {
    const collection = grid.closest('.collection');
    if (!collection) return;

    collection.querySelector('.pagination-wrapper, [data-collection-load-more-wrap]')?.remove();
    if (pageCount <= 1 || currentPage >= pageCount) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'collection-load-more collection-search__pagination';
    wrapper.dataset.collectionLoadMoreWrap = '';
    wrapper.innerHTML = `
      <button
        class="button collection-load-more__button"
        type="button"
        data-collection-search-load-more
        data-collection-search-page="${currentPage + 1}"
      >Load More</button>
    `;
    collection.append(wrapper);
  }

  restoreCollection() {
    const params = new URLSearchParams(window.location.search);
    params.delete('collection_search');
    params.delete('search_page');
    this.requestId += 1;
    FacetFiltersForm.renderPage(params.toString(), null, false);
  }

  setLoading(isLoading) {
    const container = document.getElementById('ProductGridContainer');
    container?.classList.toggle('collection-search--loading', isLoading);
    container?.querySelector('.collection')?.classList.toggle('loading', isLoading);

    document.querySelectorAll('[data-collection-search-input]').forEach((input) => {
      input.setAttribute('aria-busy', String(isLoading));
    });
  }

  buildBrowserUrl(params) {
    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  escapeHtml(value) {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const root = document.querySelector('[data-collection-search-root]');
  if (root) new CollectionProductSearch(root);
});
